import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ExclamationTriangleIcon, ExclamationCircleIcon, InformationCircleIcon, CheckCircleIcon, ArrowPathIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ServiceError } from '../../services/utils/apiErrorUtils';

export type NotificationType = 'error' | 'warning' | 'info' | 'success';

export interface ErrorNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  timestamp: number;
  statusCode?: number;
  actions?: NotificationAction[];
  autoClose?: boolean;
  duration?: number;
}

export interface NotificationAction {
  label: string;
  onClick: () => void;
  variant?: 'primary' | 'secondary';
  icon?: React.ReactNode;
}

interface ErrorNotificationProps {
  notification: ErrorNotification;
  onClose: (id: string) => void;
  onAction?: (id: string, actionIndex: number) => void;
}

const ErrorNotificationComponent: React.FC<ErrorNotificationProps> = ({
  notification,
  onClose,
  onAction,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    // Animate in
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // Auto close if specified
    if (notification.autoClose !== false) {
      const duration = notification.duration || (notification.type === 'error' ? 8000 : 5000);
      const timer = setTimeout(() => handleClose(), duration);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleClose = useCallback(() => {
    setIsExiting(true);
    setTimeout(() => onClose(notification.id), 300); // Match animation duration
  }, [notification.id, onClose]);

  const handleActionClick = (actionIndex: number) => {
    const action = notification.actions?.[actionIndex];
    action?.onClick();
    onAction?.(notification.id, actionIndex);
  };

  const getIcon = () => {
    switch (notification.type) {
      case 'error':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-5 w-5 text-yellow-500" />;
      case 'success':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'info':
      default:
        return <InformationCircleIcon className="h-5 w-5 text-blue-500" />;
    }
  };

  const getBackgroundColor = () => {
    switch (notification.type) {
      case 'error':
        return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
      case 'warning':
        return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
      case 'success':
        return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
      case 'info':
      default:
        return 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800';
    }
  };

  const getTextColor = () => {
    switch (notification.type) {
      case 'error':
        return 'text-red-800 dark:text-red-200';
      case 'warning':
        return 'text-yellow-800 dark:text-yellow-200';
      case 'success':
        return 'text-green-800 dark:text-green-200';
      case 'info':
      default:
        return 'text-blue-800 dark:text-blue-200';
    }
  };

  return (
    <div
      className={`
        max-w-md w-full pointer-events-auto transform transition-all duration-300 ease-in-out
        ${isVisible && !isExiting ? 'translate-x-0 opacity-100 scale-100' : 'translate-x-full opacity-0 scale-95'}
      `}
    >
      <div className={`rounded-lg border shadow-lg backdrop-blur-sm ${getBackgroundColor()}`}>
        <div className="p-4">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0 pt-0.5">
              {getIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`text-sm font-medium ${getTextColor()} flex items-center flex-wrap`}>
                <span className="break-words">{notification.title}</span>
                {notification.statusCode && (
                  <span className="ml-2 text-xs opacity-75 flex-shrink-0">
                    ({notification.statusCode})
                  </span>
                )}
              </div>
              <div className={`mt-1 text-sm ${getTextColor()} opacity-90 break-words leading-relaxed`}>
                {notification.message}
              </div>
              
              {/* Actions */}
              {notification.actions && notification.actions.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {notification.actions.map((action, index) => {
                    const getActionClasses = () => {
                      if (action.variant === 'primary') {
                        switch (notification.type) {
                          case 'error':
                            return 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500';
                          case 'warning':
                            return 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500';
                          case 'success':
                            return 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500';
                          default:
                            return 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500';
                        }
                      } else {
                        return 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 focus:ring-gray-500';
                      }
                    };

                    return (
                      <button
                        key={index}
                        onClick={() => handleActionClick(index)}
                        className={`
                          inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md
                          transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2
                          ${getActionClasses()}
                        `}
                      >
                        {action.icon && <span className="mr-1.5">{action.icon}</span>}
                        {action.label}
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Timestamp */}
              <div className={`mt-2 text-xs ${getTextColor()} opacity-60`}>
                {new Date(notification.timestamp).toLocaleTimeString()}
              </div>
            </div>
            <div className="flex-shrink-0">
              <button
                onClick={handleClose}
                className={`
                  rounded-md p-1 text-sm focus:outline-none focus:ring-2 focus:ring-offset-2
                  ${getTextColor()} opacity-60 hover:opacity-100 transition-opacity
                  hover:bg-black hover:bg-opacity-5 dark:hover:bg-white dark:hover:bg-opacity-5
                  focus:ring-gray-500
                `}
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Notification Manager Component
interface ErrorNotificationManagerProps {
  notifications: ErrorNotification[];
  onClose: (id: string) => void;
  onAction?: (id: string, actionIndex: number) => void;
  position?: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
  maxNotifications?: number;
}

export const ErrorNotificationManager: React.FC<ErrorNotificationManagerProps> = ({
  notifications,
  onClose,
  onAction,
  position = 'top-right',
  maxNotifications = 5,
}) => {
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'top-right':
      default:
        return 'top-4 right-4';
    }
  };

  const visibleNotifications = notifications.slice(0, maxNotifications);

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div
      className={`fixed z-50 pointer-events-none ${getPositionClasses()}`}
      aria-live="assertive"
    >
      <div className="flex flex-col space-y-3">
        {visibleNotifications.map((notification) => (
          <ErrorNotificationComponent
            key={notification.id}
            notification={notification}
            onClose={onClose}
            onAction={onAction}
          />
        ))}
        
        {/* Show count if there are more notifications */}
        {notifications.length > maxNotifications && (
          <div className="max-w-md w-full pointer-events-auto">
            <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg backdrop-blur-sm p-3">
              <div className="text-sm text-gray-600 dark:text-gray-400 text-center">
                + {notifications.length - maxNotifications} more notification{notifications.length - maxNotifications !== 1 ? 's' : ''}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Hook for managing notifications
export const useErrorNotifications = () => {
  const [notifications, setNotifications] = useState<ErrorNotification[]>([]);

  const addNotification = useCallback((notification: Omit<ErrorNotification, 'id' | 'timestamp'>) => {
    const newNotification: ErrorNotification = {
      ...notification,
      id: crypto.randomUUID(),
      timestamp: Date.now(),
    };

    setNotifications(prev => [newNotification, ...prev]);
    return newNotification.id;
  }, []);

  const removeNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  // Helper method to create error notifications from ServiceError
  const addError = useCallback((error: ServiceError, title?: string) => {
    const actions: NotificationAction[] = [];

    // Add retry action for certain error types
    if (error.statusCode === 429 || error.statusCode === 0 || (error.statusCode && error.statusCode >= 500)) {
      actions.push({
        label: 'Retry',
        onClick: () => window.location.reload(),
        variant: 'primary',
        icon: <ArrowPathIcon className="h-3 w-3" />,
      });
    }

    // Add support action for persistent errors - DISABLED FOR NOW
    // TODO: Enable when contact support functionality is configured
    // if (error.statusCode && error.statusCode >= 400) {
    //   actions.push({
    //     label: 'Contact Support',
    //     onClick: () => {
    //       // This could open a support modal or redirect to support page
    //       console.log('Contact support clicked', error);
    //     },
    //     variant: 'secondary',
    //     icon: <ArrowTopRightOnSquareIcon className="h-3 w-3" />,
    //   });
    // }

    return addNotification({
      type: 'error',
      title: title || 'Request Failed',
      message: error.message,
      statusCode: error.statusCode,
      actions: actions.length > 0 ? actions : undefined,
      autoClose: error.statusCode !== 401, // Don't auto-close auth errors
    });
  }, [addNotification]);

  return {
    notifications,
    addNotification,
    addError,
    removeNotification,
    clearAll,
  };
};

export default ErrorNotificationComponent;